import { useState, type ReactNode } from 'react'
import {
  ArrowChevronRightIcon,
  CurecallBadge,
  CurecallTag,
  ECurecallBadgeColor,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { OutboundSmsLogCard } from 'src/components/sms-modal/outbound-sms-log-card.component'
import { SmsLogCard } from 'src/components/sms-modal/sms-log-card.component'
import { useLocale } from 'src/hooks/use-locale'
import { MED_ALERT_UI_TO_TAG_STATUS } from 'src/types/med-alert'
import { TimezoneAwareTime } from 'src/components/timezone-aware-time/timezone-aware-time.component'
import { getFormattedDateWithHours } from 'src/utils/date-formatter.util'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { twMerge } from 'src/utils/tw-merge.util'
import {
  EHumanReviewRequestProblem,
  type THumanReviewDetail,
  type THumanReviewRequest,
} from '../human-review-requests.type'
import { isResolvedRequest, ResolutionNote } from '../resolution/resolution-note.component'
import { useProblemLabels } from '../use-problem-labels'
import { ConversationDetail } from './conversation-detail.component'
import {
  buildProblemRows,
  getCardSource,
  getConvocationDateChange,
  getRequestDetails,
  hasConversationDetail,
  type TConvocationDateChange,
  type TNewConvocationDate,
  type TProblemRow,
} from './problem-card.util'

const ProblemTags = ({
  row: { request, requests },
  displayedVariableIds,
  isResolved,
}: {
  row: TProblemRow<THumanReviewRequest>
  // Variables already displayed as a line of the conversation
  displayedVariableIds: number[]
  isResolved: boolean
}) => {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const problemLabels = useProblemLabels()

  return (
    <>
      <CurecallTag status={isResolved ? 'SUCCESS' : 'WARNING'} isDeletable={false} className="c-text-xs">
        {getRequestDetails(request).isPatientFeedback
          ? t('pages.dashboard.human-review-requests.problems.unknown.patient-feedback')
          : problemLabels[request.problem]}
      </CurecallTag>
      {requests.map((rowRequest) => {
        const { variable } = getRequestDetails(rowRequest)
        return (
          variable &&
          !displayedVariableIds.includes(variable.id) && (
            <CurecallBadge key={rowRequest.id} color={ECurecallBadgeColor.OUTLINED_RED} className="c-text-xs">
              {getTranslatedLabel(locale, variable.intlLabel)}
            </CurecallBadge>
          )
        )
      })}
    </>
  )
}

const MedAlertTags = ({ row: { requests } }: { row: TProblemRow<THumanReviewRequest> }) =>
  requests.map((request) => {
    const { medAlert } = getRequestDetails(request)
    return (
      medAlert && (
        <CurecallTag
          key={request.id}
          status={MED_ALERT_UI_TO_TAG_STATUS[medAlert.MedAlert.ui]}
          isDeletable={false}
          className="c-text-xs"
        >
          {medAlert.MedAlert.label}
        </CurecallTag>
      )
    )
  })

const BodyItem = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={twMerge('c-bg-white c-border c-border-grey-200 c-rounded-lg c-px-4 c-py-3', className)}>
    {children}
  </div>
)

const ConvocationDateChangeBody = ({
  convocationDateChange: { smsLog, newDate },
}: {
  convocationDateChange: TConvocationDateChange
}) => {
  const { t } = useTranslation()
  return (
    <div className="c-grid c-grid-cols-2 c-gap-2">
      {smsLog && (
        <BodyItem className="c-min-w-0">
          <p className="c-text-sm c-font-medium c-mt-0 c-mb-1">
            {t(
              'pages.dashboard.human-review-requests.problems.convocation-date-changed.sms-sent-with-old-date',
            )}
          </p>
          <OutboundSmsLogCard smsLog={smsLog} className="c-w-full" />
        </BodyItem>
      )}
      {newDate && (
        <BodyItem className="c-min-w-0 c-col-start-2">
          <p className="c-text-sm c-font-medium c-mt-0 c-mb-1">
            {
              {
                convocation: t(
                  'pages.dashboard.human-review-requests.problems.convocation-date-changed.new-convocation-date',
                ),
                appointment: t(
                  'pages.dashboard.human-review-requests.problems.convocation-date-changed.new-appointment-date',
                ),
              }[newDate.kind]
            }
          </p>
          <p className="c-text-base c-font-semibold c-my-0">
            <TimezoneAwareTime date={newDate.date} formatter="dateWithHours" />
          </p>
        </BodyItem>
      )}
    </div>
  )
}

const RequestBodyItems = ({
  request,
  patient,
}: {
  request: THumanReviewRequest
  patient: THumanReviewDetail['patient']
}) => {
  const { t } = useTranslation()
  const { smsLogs, hasNoContext } = getRequestDetails(request)
  const smsPatient = { id: patient.id, firstname: patient.firstName ?? '', lastname: patient.lastName ?? '' }

  return (
    <>
      {smsLogs.map((smsLog) => (
        <BodyItem key={smsLog.id}>
          {request.problem === EHumanReviewRequestProblem.SMS_NOT_RECEIVED ? (
            <OutboundSmsLogCard smsLog={smsLog} className="c-w-full" />
          ) : (
            <SmsLogCard smsLog={smsLog} patient={smsPatient} />
          )}
        </BodyItem>
      ))}
      {hasNoContext && (
        <BodyItem className="c-text-sm c-text-grey-700">
          {t('pages.dashboard.human-review-requests.problems.unknown.no-context')}
        </BodyItem>
      )}
    </>
  )
}

type Props = {
  // The requests are all from the same conversation, or are aggregated problems without conversation
  requests: THumanReviewRequest[]
  patient: THumanReviewDetail['patient']
  newDate?: TNewConvocationDate | null
  defaultExpanded?: boolean
}

export const ProblemCard = ({ requests, patient, newDate = null, defaultExpanded = true }: Props) => {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const resolvedRequest = requests.find(isResolvedRequest)
  const conversation = requests.find(hasConversationDetail)?.PatientConversation ?? null
  const missingDocumentVariableIds = requests
    .filter((request) => request.problem === EHumanReviewRequestProblem.MISSING_DOCUMENT)
    .flatMap((request) => (request.Variable ? [request.Variable.id] : []))
  const displayedVariableIds = conversation?.questions.map((question) => question.variableId) ?? []

  // Displayed above the conversation questions
  const convocationDateChanges = requests.flatMap((request) => {
    const convocationDateChange = getConvocationDateChange(request, newDate)
    return convocationDateChange ? [{ request, convocationDateChange }] : []
  })

  const hasBody =
    !!conversation ||
    convocationDateChanges.length > 0 ||
    requests.some((request) => {
      const { smsLogs, hasNoContext } = getRequestDetails(request)
      return smsLogs.length > 0 || hasNoContext
    })

  const source = getCardSource(requests, locale)
  const sourceTexts =
    source &&
    {
      conversation: {
        title: source.label,
        sentAt: t('pages.dashboard.human-review-requests.problems.incomplete-questionnaire.sent-at', {
          date: getFormattedDateWithHours(t, source.sentAt),
        }),
      },
      workflow: {
        title: t('pages.dashboard.human-review-requests.problems.workflow-title', { label: source.label }),
        sentAt: t('pages.dashboard.human-review-requests.problems.workflow-sent-at', {
          date: getFormattedDateWithHours(t, source.sentAt),
        }),
      },
    }[source.kind]
  const title = source?.label ? sourceTexts?.title : null
  const sentAtLabel = source?.sentAt ? sourceTexts?.sentAt : null

  const bodyItems = (
    <>
      {convocationDateChanges.map(({ request, convocationDateChange }) => (
        <ConvocationDateChangeBody key={request.id} convocationDateChange={convocationDateChange} />
      ))}
      {conversation && (
        <ConversationDetail
          conversation={conversation}
          missingDocumentVariableIds={missingDocumentVariableIds}
          hasMissingDocument={requests.some(
            (request) => request.problem === EHumanReviewRequestProblem.MISSING_DOCUMENT,
          )}
        />
      )}
      {requests.map((request) => (
        <RequestBodyItems key={request.id} request={request} patient={patient} />
      ))}
    </>
  )

  const problemRows = buildProblemRows(requests)

  const headerContent = (
    <>
      {(title || sentAtLabel) && (
        <div className="c-flex c-flex-col c-items-start c-gap-1 c-shrink-0">
          {title && <span className="c-text-base c-text-left c-whitespace-nowrap">{title}</span>}
          {sentAtLabel && (
            <p className="c-text-sm c-font-light c-text-grey-700 c-text-left c-m-0">{sentAtLabel}</p>
          )}
        </div>
      )}
      <span
        className={twMerge(
          'c-flex c-items-center c-gap-2 c-flex-wrap c-justify-end c-min-w-0',
          (title || sentAtLabel) && 'c-ml-auto',
        )}
      >
        {problemRows.map((row) => (
          <ProblemTags
            key={row.request.id}
            row={row}
            displayedVariableIds={displayedVariableIds}
            isResolved={!!resolvedRequest}
          />
        ))}
        {problemRows.map((row) => (
          <MedAlertTags key={row.request.id} row={row} />
        ))}
      </span>
    </>
  )

  return (
    <div className="c-flex c-flex-col c-bg-white c-border c-border-grey-300 c-rounded-xl c-p-4 c-ml-4">
      <div className="c-flex c-items-center c-gap-2">
        {hasBody ? (
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-label={
              isExpanded
                ? t('pages.dashboard.human-review-requests.problems.hide-detail')
                : t('pages.dashboard.human-review-requests.problems.show-more-detail')
            }
            onClick={() => setIsExpanded((previous) => !previous)}
            className="c-flex c-flex-1 c-items-start c-gap-3 c-cursor-pointer c-border-none c-bg-transparent c-p-0"
          >
            {headerContent}
            <ArrowChevronRightIcon
              className={twMerge(
                'c-h-4 c-shrink-0 c-text-grey-700 c-transition-transform c-duration-300 c-ease-in-out motion-reduce:c-transition-none',
                isExpanded ? 'c-rotate-[270deg]' : 'c-rotate-90',
              )}
            />
          </button>
        ) : (
          <div className="c-flex c-flex-1 c-items-start c-gap-3">{headerContent}</div>
        )}
      </div>
      {resolvedRequest && <ResolutionNote request={resolvedRequest} />}
      {hasBody && (
        <div
          aria-hidden={!isExpanded}
          className={twMerge(
            'c-grid c-transition-[grid-template-rows,opacity,visibility] c-duration-300 c-ease-in-out motion-reduce:c-transition-none',
            isExpanded ? 'c-grid-rows-[1fr] c-opacity-100' : 'c-grid-rows-[0fr] c-opacity-0 c-invisible',
          )}
        >
          <div className="c-min-h-0 c-overflow-hidden">
            <div
              className={twMerge(
                'c-bg-[#f0f5ff] c-rounded-xl c-p-4 c-mt-2',
                convocationDateChanges.length > 0 ? 'c-w-full' : 'c-w-1/2',
              )}
            >
              <div className="c-flex c-flex-col c-gap-2">{bodyItems}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
